import React from "react";
import { Box, Typography, Alert } from "@mui/material";
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import PaymentQRCode from "@/components/PaymentQRCode";
import { useFitpassPage } from "../../hooks/useFitpassPage";
import AddMembershipForm from "./components/AddMembershipForm";
import FitpassRequestsTable from "./components/FitpassRequestsTable";
import FitpassRequestEditDialog from "./components/FitpassRequestEditDialog";
import CopyToNextMonthDialog from "./components/CopyToNextMonthDialog";

const Fitpass: React.FC = () => {
  const {
    isAdmin,
    isLoading,
    isLoadingEmployees,
    isCopying,
    isUpdating,
    employees,
    success,
    error,
    selectedEmployee,
    setSelectedEmployee,
    createMonth,
    setCreateMonth,
    defaultMonthLabel,
    createMonthOptions,
    additionalPeople,
    filteredRequests,
    selectedMonth,
    setSelectedMonth,
    filterMonthOptions,
    isViewingCurrentMonth,
    nextMonthLabel,
    copyDialogOpen,
    setCopyDialogOpen,
    editDialogOpen,
    editEmployee,
    setEditEmployee,
    editAdditionalPeople,
    deleteDialogOpen,
    requestToDelete,
    canEditRequestPayment,
    handleAddPerson,
    handleRemovePerson,
    handlePersonChange,
    handleSubmit,
    handleEditClick,
    handleEditClose,
    handleEditAddPerson,
    handleEditRemovePerson,
    handleEditPersonChange,
    handleEditSubmit,
    handleDeleteClick,
    handleDeleteConfirm,
    handleDeleteCancel,
    handlePaidToggle,
    handleAdditionalPersonPaidToggle,
    handleCopyToNextMonth,
  } = useFitpassPage();

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Fitpass Membership
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Admins manage memberships; employees can view their membership and
        confirm payments.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 3,
          mb: 4,
          flexWrap: "wrap",
          alignItems: "stretch",
        }}
      >
        {isAdmin && (
          <AddMembershipForm
            employees={employees}
            isLoadingEmployees={isLoadingEmployees}
            selectedEmployee={selectedEmployee}
            onEmployeeChange={setSelectedEmployee}
            createMonth={createMonth}
            defaultMonthLabel={defaultMonthLabel}
            createMonthOptions={createMonthOptions}
            onCreateMonthChange={setCreateMonth}
            additionalPeople={additionalPeople}
            onAddPerson={handleAddPerson}
            onRemovePerson={handleRemovePerson}
            onPersonChange={handlePersonChange}
            onSubmit={handleSubmit}
          />
        )}

        <Box
          sx={{
            flex: "1 1 0",
            minWidth: { xs: "100%", md: 300 },
            maxWidth: { xs: "100%", md: "none" },
          }}
        >
          <PaymentQRCode />
        </Box>
      </Box>

      <FitpassRequestsTable
        isAdmin={isAdmin}
        isLoading={isLoading}
        requests={filteredRequests}
        selectedMonth={selectedMonth}
        filterMonthOptions={filterMonthOptions}
        isViewingCurrentMonth={isViewingCurrentMonth}
        nextMonthLabel={nextMonthLabel}
        isCopying={isCopying}
        canEditPayment={canEditRequestPayment}
        onMonthChange={setSelectedMonth}
        onCopyClick={() => setCopyDialogOpen(true)}
        onEdit={handleEditClick}
        onDelete={handleDeleteClick}
        onPaidToggle={handlePaidToggle}
        onAdditionalPersonPaidToggle={handleAdditionalPersonPaidToggle}
      />

      <FitpassRequestEditDialog
        open={editDialogOpen}
        isUpdating={isUpdating}
        isLoadingEmployees={isLoadingEmployees}
        employees={employees}
        editEmployee={editEmployee}
        editAdditionalPeople={editAdditionalPeople}
        onClose={handleEditClose}
        onSave={handleEditSubmit}
        onEmployeeChange={setEditEmployee}
        onAddPerson={handleEditAddPerson}
        onRemovePerson={handleEditRemovePerson}
        onPersonChange={handleEditPersonChange}
      />

      <CopyToNextMonthDialog
        open={copyDialogOpen}
        isCopying={isCopying}
        selectedMonth={selectedMonth}
        nextMonthLabel={nextMonthLabel}
        requestCount={filteredRequests.length}
        onClose={() => setCopyDialogOpen(false)}
        onConfirm={handleCopyToNextMonth}
      />

      {requestToDelete && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Fitpass Request"
          itemName={`${requestToDelete.employeeName} ${requestToDelete.employeeSurname}`}
          itemType="request"
        />
      )}
    </Box>
  );
};

export default Fitpass;
